import React, { useContext, useMemo, useState } from "react";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import { InvoiceExtractionRecord } from "@common/schemas/financialDocument/invoice-extraction-record.schema";
import { useFinancialDocument } from "@api/financialDocument.api";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { Loader } from "@design-system/Loader/Loader";
import { accrualImporterDetailIncludes } from "@components/documents/financialDocumentDetailIncludes";
import importerStyles from "@components/importer/ImporterPage.module.scss";
import { AccrualImporterContext } from "../accrualImporter.context";
import { AccrualDocumentImporterMode } from "../accrualDocumentImporter.lib";
import { ImporterDocumentStatusMessage } from "@components/importer/ImporterDocumentStatusMessage";
import {
    accrualDocumentBeingProcessed,
    getAllAccrualLineItemExtractions,
    nextAccrualDraftToReviewRowId,
    resolveAccrualRowExtraction,
} from "../accrualImporter.helpers";
import { discardDraftQuestion } from "../accrualImporterDraftGroups";
import { AccrualDraftDetailsWorkspace } from "./AccrualDraftDetailsWorkspace";
import { AccrualDraftDrawerShell } from "./AccrualDraftDrawerShell";
import { useRemoveAccrualImporterDocument } from "../useRemoveAccrualImporterDocument";
import { AccrualImporterDocumentRow } from "../accrualImporter.types";

function pickAccrualDraftDocument({
    listed,
    fetched,
}: {
    listed?: FinancialDocumentExtended;
    fetched?: FinancialDocumentExtended;
}): FinancialDocumentExtended | undefined {
    if (!listed) {
        return fetched;
    }
    if (!fetched) {
        return listed;
    }
    if (
        accrualDocumentBeingProcessed(fetched) &&
        !accrualDocumentBeingProcessed(listed)
    ) {
        return listed;
    }
    return fetched;
}

function previewedExtraction({
    financialDocument,
    mode,
    extractionId,
}: {
    financialDocument: FinancialDocumentExtended;
    mode: AccrualDocumentImporterMode;
    extractionId?: string;
}): InvoiceExtractionRecord | null {
    return (
        resolveAccrualRowExtraction(financialDocument, mode, extractionId) ??
        resolveAccrualRowExtraction(financialDocument, mode)
    );
}

export const AccrualDraftDetailsDrawer: React.FC<{
    navigationRows: AccrualImporterDocumentRow[];
}> = ({ navigationRows }) => {
    const {
        mode,
        sortedRows,
        previewedRowId,
        previewedExtractionId,
        setPreviewedRowId,
    } = useContext(AccrualImporterContext);
    const previewedRow = useMemo(
        () => sortedRows.find((row) => row.id === previewedRowId),
        [sortedRows, previewedRowId],
    );
    const financialDocumentId = previewedRow?.financialDocument?.id;
    const documentQuery = useFinancialDocument(
        financialDocumentId,
        accrualImporterDetailIncludes,
    );
    const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);
    const { removeDocument } = useRemoveAccrualImporterDocument(previewedRow);
    const close = () => setPreviewedRowId(undefined);
    const openNextDraft = () => {
        if (!previewedRow) {
            return;
        }
        setPreviewedRowId(
            nextAccrualDraftToReviewRowId({
                rows: navigationRows,
                currentRowId: previewedRow.id,
            }),
        );
    };

    if (!previewedRow) {
        return null;
    }

    const financialDocument = pickAccrualDraftDocument({
        listed: previewedRow.financialDocument,
        fetched: documentQuery.data,
    });
    const isFetchingDocument =
        financialDocumentId !== undefined &&
        !documentQuery.data &&
        !documentQuery.isError;

    if (isFetchingDocument || !financialDocument) {
        return (
            <AccrualDraftDrawerShell
                financialDocument={previewedRow.financialDocument}
                navigationRows={navigationRows}
                onClose={close}
            >
                <Loader />
            </AccrualDraftDrawerShell>
        );
    }

    if (accrualDocumentBeingProcessed(financialDocument)) {
        return (
            <AccrualDraftDrawerShell
                financialDocument={financialDocument}
                navigationRows={navigationRows}
                onClose={close}
            >
                <div className={importerStyles.drawerStatus}>
                    <ImporterDocumentStatusMessage
                        icon={<Loader inline />}
                        message="Extracting"
                    />
                </div>
            </AccrualDraftDrawerShell>
        );
    }

    const extraction = previewedExtraction({
        financialDocument,
        mode,
        extractionId: previewedExtractionId,
    });
    const omittedExtractionCount = extraction
        ? getAllAccrualLineItemExtractions(financialDocument, mode).filter(
              (item) => item.id !== extraction.id,
          ).length
        : 0;
    const discardQuestion =
        omittedExtractionCount > 0
            ? discardDraftQuestion({
                  fileCount: 1,
                  omittedExtractionCount,
                  itemLabel: mode === "invoices" ? "invoice" : "bill",
              })
            : "This moves the file out of Inbox. The file stays in Documents.";

    return (
        <>
            <AccrualDraftDetailsWorkspace
                financialDocument={financialDocument}
                extraction={extraction}
                mode={mode}
                navigationRows={navigationRows}
                onClose={close}
                onDiscard={() => setIsDiscardConfirmOpen(true)}
                onImported={openNextDraft}
            />
            <ConfirmationModal
                show={isDiscardConfirmOpen}
                title="Discard draft?"
                question={discardQuestion}
                yes="Discard"
                yesBtnVariant="destructive"
                no="Cancel"
                onHide={() => setIsDiscardConfirmOpen(false)}
                onConfirm={async () => {
                    const nextRowId = nextAccrualDraftToReviewRowId({
                        rows: navigationRows,
                        currentRowId: previewedRow.id,
                    });
                    const didRemove = await removeDocument();
                    setIsDiscardConfirmOpen(false);
                    if (didRemove) {
                        setPreviewedRowId(nextRowId);
                    }
                }}
            />
        </>
    );
};
